import { useTranslations } from "next-intl";
import styles from "./video-uploader.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone, FileRejection, ErrorCode } from "react-dropzone";
import { MdDelete, MdErrorOutline } from "react-icons/md";
import { handleDeleteVideo, handleUploadListingVideo } from "./handler";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { FaVideo } from "react-icons/fa6";
import { nanoid } from "nanoid";
import { toast } from "react-toastify";
import { ListingTypeEnum } from "@/types/types";
import { useEdittingStore } from "../../context/useEdittingStore";

export default function VideoUploader() {
  const { userData } = useUserAuthDataContext();
  const t = useTranslations("NewListing.videoUploader");
  const videoError = useEdittingStore(
    (state) => state.validationErrors.presentation_video_data
  );
  const listing_id = useEdittingStore((state) => state.listing.listing_id);
  const listing_type = useEdittingStore((state) => state.listing.listing_type);
  const setField = useEdittingStore((state) => state.setField);
  const presentation_video_data = useEdittingStore(
    (state) => state.listing.presentation_video_data
  );
  const numberOfMaxSizeRef = useRef(20);
  const [numberOfMaxVideoSizeUI, setNumberOfMaxVideoSizeUI] = useState(20);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    let maxVideoSize = 30;
    if (listing_type === ListingTypeEnum.PRIME) {
      maxVideoSize = 65;
    }
    if (listing_type === ListingTypeEnum.SPOTLIGHT) {
      maxVideoSize = 100;
    }
    numberOfMaxSizeRef.current = maxVideoSize;
    setNumberOfMaxVideoSizeUI(maxVideoSize);
    if (presentation_video_data?.video_url !== "") {
      setField("presentation_video_data", presentation_video_data);
    }
  }, [listing_type]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length !== 0) {
        switch (rejectedFiles[0].errors[0].code) {
          case ErrorCode.FileInvalidType:
            toast(t("formatError"), {
              type: "error",
            });
            break;
          case ErrorCode.FileTooLarge:
            toast(t("tooLargeError", { maxSize: numberOfMaxSizeRef.current }), {
              type: "error",
            });
            break;
          case ErrorCode.TooManyFiles:
            toast(t("tooManyFiles"), {
              type: "error",
            });
            break;
          case ErrorCode.FileTooSmall:
            toast(t("fileTooSmall"), {
              type: "error",
            });
            break;
          default:
            toast(t("unknownError"), {
              type: "error",
            });
            break;
        }
        return;
      }

      // accept first file (single upload)
      if (acceptedFiles.length > 0) {
        const videoFile = acceptedFiles[0];
        const idString = nanoid(12);

        // Preserve extension
        const ext = videoFile.name.split(".").pop() || "mp4";
        const newFile = new File([videoFile], `${idString}.${ext}`, {
          type: videoFile.type,
        });

        setField("presentation_video_data", {
          video_url: URL.createObjectURL(newFile),
          thumbnail_url: "",
        });
        setUploadProgress(0);
        await handleUploadListingVideo(
          newFile,
          listing_id,
          userData.sessionId,
          setField,
          setUploadProgress
        );
      }
    },
    [listing_id, userData, setField]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "video/mp4": [".mp4"],
      "video/webm": [".webm"],
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024,
    minSize: 1 * 1024 * 1024,
  });

  return (
    <>
      <h3
        id="photos"
        className={`${styles.fieldTitle2} ${videoError && styles.error}`}
      >
        {t("addVideo")}
      </h3>
      {videoError ? (
        <p className={styles.errorText}>
          {videoError === "tooManyVideos" ? t("tooManyVideos") : t(videoError)}
          <span>
            <MdErrorOutline />
          </span>
        </p>
      ) : (
        <p className={styles.fieldDescription}>{t("addVideoSubtitle")}</p>
      )}
      <div
        className={`${styles.videoUploader} ${
          presentation_video_data.video_url ? styles.hasVideo : ""
        }`}
      >
        <div
          className={`${styles.addVideoWrapper} ${videoError && styles.error}`}
          {...getRootProps()}
        >
          {presentation_video_data.video_url ? (
            <div className={styles.videoPreviewWrapper}>
              <video
                src={presentation_video_data.video_url}
                className={styles.videoPreview}
                controls
                width="100%"
                poster={presentation_video_data.thumbnail_url}
              />
              {uploadProgress !== null && (
                <div className={styles.uploadProgress}>
                  <p>{uploadProgress}%</p>
                </div>
              )}
              <button
                className={styles.deleteButton}
                onClick={async (e) => {
                  e.preventDefault();
                  const isConfirmed = window.confirm(t("deleteText"));
                  //delete video
                  if (isConfirmed) {
                    await handleDeleteVideo(
                      listing_id,
                      presentation_video_data,
                      userData.sessionId,
                      setField
                    );
                    return;
                  } else {
                    return;
                  }
                }}
              >
                <MdDelete className={styles.icon} />
              </button>
            </div>
          ) : (
            <>
              {<input {...getInputProps()} />}
              <FaVideo className={styles.videoIcon} />
              {isDragActive ? (
                <span className={styles.addVideoDescription}>
                  {t("dropVideoHere")}
                </span>
              ) : (
                <span className={styles.addVideoDescription}>
                  {t("addVideoDescription", {
                    maxSize: numberOfMaxVideoSizeUI,
                  })}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
